import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";

function today() { return new Date().toISOString().slice(0, 10); }
function monthStart() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }

export default function ProfitLoss() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/profit-loss?from_date=${from}&to_date=${to}`)
      .then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  }, [from, to]);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><TrendingUp className="w-3 h-3"/> Owner-only · Bottom line for the workshop</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Profit & Loss</h1>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label><Input data-testid="from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label><Input data-testid="to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-border bg-card rounded-sm p-5 col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Statement</div>
            <div className="space-y-3 text-sm">
              <div className="border-b border-border pb-2">
                <div className="flex justify-between font-semibold text-green-500 mb-2"><span>Revenue</span><span className="font-mono" data-testid="revenue-total">{inr(data.revenue.total)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground pl-4"><span>Parts Sales</span><span className="font-mono">{inr(data.revenue.parts_sales)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground pl-4"><span>Service Labour</span><span className="font-mono">{inr(data.revenue.service_labour)}</span></div>
              </div>
              <div className="border-b border-border pb-2">
                <div className="flex justify-between text-destructive"><span>Less: Cost of Goods Sold (Purchases)</span><span className="font-mono">-{inr(data.cogs.purchases)}</span></div>
              </div>
              <div className="flex justify-between font-semibold border-b border-border pb-2">
                <span>Gross Profit</span><span className="font-mono" data-testid="gross-profit">{inr(data.gross_profit)}</span>
              </div>
              <div className="border-b border-border pb-2">
                <div className="flex justify-between text-destructive mb-2"><span>Less: Operating Expenses</span><span className="font-mono">-{inr(data.expenses.total)}</span></div>
                {Object.entries(data.expenses.by_category).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs text-muted-foreground pl-4"><span>{k}</span><span className="font-mono">{inr(v)}</span></div>
                ))}
                {Object.keys(data.expenses.by_category).length === 0 && (
                  <div className="text-xs text-muted-foreground pl-4 italic">No expenses recorded</div>
                )}
              </div>
              <div className={`flex justify-between font-display font-black text-2xl pt-2 ${data.net_profit >= 0 ? "text-primary" : "text-destructive"}`}>
                <span>{data.net_profit >= 0 ? "Net Profit" : "Net Loss"}</span>
                <span className="font-mono" data-testid="net-profit">{inr(data.net_profit)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="border border-green-500/40 bg-green-500/5 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-green-500">Revenue</div>
              <div className="font-display font-black text-2xl text-green-500 mt-1">{inr(data.revenue.total)}</div>
            </div>
            <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-destructive">Total Costs</div>
              <div className="font-display font-black text-2xl text-destructive mt-1">{inr(data.cogs.purchases + data.expenses.total)}</div>
            </div>
            <div className={`border rounded-sm p-4 ${data.net_profit >= 0 ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`}>
              <div className={`text-[10px] uppercase tracking-[0.2em] ${data.net_profit >= 0 ? "text-primary" : "text-destructive"}`}>{data.net_profit >= 0 ? "Net Profit" : "Net Loss"}</div>
              <div className={`font-display font-black text-3xl mt-1 ${data.net_profit >= 0 ? "text-primary" : "text-destructive"}`}>{inr(data.net_profit)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
